import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Material-UI Components
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Custom Components
import { SFStack } from "../grid";
import SFTypography from "../typography";
import SFIconButton from "../button/iconButton";
import SFButton from "../button";
import SFAsyncActionButton from "../button/asyncActionButton";
import SFHaloIconWrapper from "../icons/halo";
import {
  SFCloseIcon,
  SFErrorIcon,
  SFInfoIconFilled,
  SFWarningIcon,
} from "../icons";

// Custom Hooks
import getStyled from "../../hooks/getStyled";

const { styled } = getStyled();

const StyledDialog = styled(Dialog)(({ theme }) => ({
  ".MuiPaper-root": {
    width: "100%",
    "& .MuiDialogContent-root": {
      padding: theme.spacing(2.5),
      justifyContent: "flex-start",
      flexWrap: "wrap",
    },
  },
}));

const Content = ({ message, variant, icon }) => {
  const VARIANT_TO_ICON = {
    error: <SFErrorIcon color="red300" />,
    info: <SFInfoIconFilled color="blue400" />,
    warning: <SFWarningIcon color="orange200" rounded />,
  };

  const variantIcon = (
    <SFHaloIconWrapper>{VARIANT_TO_ICON[variant]}</SFHaloIconWrapper>
  );

  return (
    <SFStack direction="row" spacing={1.5} alignItems="center">
      {icon ?? (VARIANT_TO_ICON[variant] && variantIcon)}
      <SFTypography variant="body1" hasHtml>{message}</SFTypography>
    </SFStack>
  );
};

const SFDialog = (props) => {
  const { t } = useTranslation();

  const VARIANT_TO_TITLE = {
    error: t("components.dialog.alert"),
    info: t("components.dialog.confirmation"),
    warning: t("components.dialog.confirmation"),
  };

  return (
    <StyledDialog open={props.open} maxWidth={props.maxWidth} fullWidth>
      <DialogTitle>
        <SFStack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          height="100%"
        >
          <SFTypography variant="h3">
            {props.title ?? VARIANT_TO_TITLE[props.variant]}
          </SFTypography>
          {props.closeButton && (
            <SFIconButton onClick={props.onClose}>
              <SFCloseIcon color="grey450" />
            </SFIconButton>
          )}
        </SFStack>
      </DialogTitle>
      <DialogContent>
        {props.message ? (
          <Content
            variant={props.variant}
            icon={props.icon}
            message={props.message}
          />
        ) : (
          props.children
        )}
      </DialogContent>
      {props.customDialogActions && (
        <DialogActions>{props.customDialogActions}</DialogActions>
      )}
      {!props.customDialogActions &&
        (props.primaryButton || props.secondaryButton) && (
        <DialogActions>
          <SFStack direction="row" spacing={1.25}>
            {props.secondaryButton &&
                (props.secondaryButtonProps?.async ? (
                  <SFAsyncActionButton
                    variant="secondary"
                    onClick={props.secondaryButtonProps?.onClick}
                    disabled={props.secondaryButtonProps?.disabled ?? false}
                    onError={props.secondaryButtonProps?.onError}
                  >
                    {props.secondaryButtonProps?.label ??
                      t("components.dialog.cancel")}
                  </SFAsyncActionButton>
                ) : (
                  <SFButton
                    variant="secondary"
                    onClick={props.secondaryButtonProps?.onClick}
                    disabled={props.secondaryButtonProps?.disabled ?? false}
                  >
                    <SFTypography variant="buttonDefault">
                      {props.secondaryButtonProps?.label ??
                        t("components.dialog.cancel")}
                    </SFTypography>
                  </SFButton>
                ))}
            {props.primaryButton &&
                (props.primaryButtonProps?.async ? (
                  <SFAsyncActionButton
                    variant="primary"
                    onClick={props.primaryButtonProps?.onClick}
                    disabled={props.primaryButtonProps?.disabled ?? false}
                    onError={props.primaryButtonProps?.onError}
                  >
                    {props.primaryButtonProps?.label ??
                      (props.secondaryButton
                        ? t("components.dialog.confirm")
                        : t("components.dialog.ok"))}
                  </SFAsyncActionButton>
                ) : (
                  <SFButton
                    variant="primary"
                    onClick={props.primaryButtonProps?.onClick}
                    disabled={props.primaryButtonProps?.disabled ?? false}
                  >
                    <SFTypography variant="buttonDefault">
                      {props.primaryButtonProps?.label ??
                        (props.secondaryButton
                          ? t("components.dialog.confirm")
                          : t("components.dialog.ok"))}
                    </SFTypography>
                  </SFButton>
                ))}
          </SFStack>
        </DialogActions>
      )}
    </StyledDialog>
  );
};

export default SFDialog;

SFDialog.propTypes = {
  /** If `true`, the dialog will be visible. */
  open: PropTypes.bool.isRequired,
  /** The child node of the dialog component to display in the content area if no message is provided. */
  children: PropTypes.node,
  /** The custom actions to display in the dialog. This will override the primary and secondary buttons. */
  customDialogActions: PropTypes.node,
  /** If `true`, the close button will be displayed in the top right corner of the dialog. */
  closeButton: PropTypes.bool,
  /** Callback fired when the close or secondary button is clicked. */
  onClose: PropTypes.func,
  /** The icon to display in the content area if the `variant` is `default"`. */
  icon: PropTypes.node,
  /** The maximum width of the dialog. */
  maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", false]),
  /** The message to display in the content area if the `variant` is `"error"`, `"info"` or `"warning"`. */
  message: PropTypes.string,
  /** If `true`, the primary button will be displayed. */
  primaryButton: PropTypes.bool,
  /** The props for the primary button:
   * - `label`: The label of the primary button.
   * - `disabled`: If `true`, the primary button will be disabled.
   * - `onClick`: The callback function to be executed when the primary button is clicked.
   * - `async`: If `true`, the primary button will be an async action button.
   * - `onError`: The function to call when the async action button has an error (optional).
   */
  primaryButtonProps: PropTypes.shape({
    label: PropTypes.string,
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
    async: PropTypes.bool,
    onError: PropTypes.func,
  }),
  /** If `true`, the secondary button will be displayed. */
  secondaryButton: PropTypes.bool,
  /** The props for the secondary button:
   * - `label`: The label of the secondary button.
   * - `disabled`: If `true`, the secondary button will be disabled.
   * - `onClick`: The callback function to be executed when the secondary button is clicked.
   * - `async`: If `true`, the secondary button will be an async action button.
   * - `onError`: The function to call when the async action button has an error (optional).
   */
  secondaryButtonProps: PropTypes.shape({
    label: PropTypes.string,
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
    async: PropTypes.bool,
    onError: PropTypes.func,
  }),
  /** The title to display in the dialog (overrides the default title of the `variant`). */
  title: PropTypes.string,
  /** The variant of the dialog:
   * - `"default"` will display the `message` with the icon passed (if one is provided) in the content area.
   * - `"error"`, `"info"` and `"warning"` will display the `message` with the corresponding icon in the content area and the corresponding title.
   */
  variant: PropTypes.oneOf(["default", "error", "info", "warning"]),
};

SFDialog.defaultProps = {
  closeButton: true,
  maxWidth: "sm",
  primaryButton: false,
  secondaryButton: false,
  variant: "default",
};
